/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 7 hook(s) need dependency review; fix each and remove this directive */
import {
  createContainer,
  createMergeEstimate,
  createMergeFile,
  emptyCurrentFolderInFileManager,
  minimiseFileUpload,
  processFileList,
  selectedFilesFileManager,
  setCurrentFolderInFileManager,
  refreshFileManager as refreshFileManagerFn,
} from "@Actions/fileUpload";
import axiosWrapper from "@AxiosWrapper";
import {
  DRAWING_TYPE,
  PDF_ENGINE,
  PDF_TYPE,
  SOMETHING_WENT_WRONG,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_SUCCESS,
  TOAST_TYPE_WARNING,
} from "@Services/constants";
import { getBackendUrl } from "@Utils";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import { useDispatch, useSelector } from "react-redux";
import { setSpaceToggle } from "@Reducers/AccessPermissionReducer";
import _ from "lodash";
import type {
  GetFileVersionDataResponse,
  ImportExternalVolumeFilesBody,
  StandardResponse,
} from "@Types/fileOperations";
import { showToastNotification } from "@Reducers/toastSlice";
import { getViewerUrl, MAX_FILE_UPLOAD_SIZE, buildAllowedExtensionsAccept } from "@Helpers";
import { setViewerState } from "@Reducers/Viewer";
import {
  getContractDetailUrl,
  getEstimateUrl,
  getScheduleUrl,
  isFileNomenclatureCorrect,
} from "@Services/utils/helpers";
import { downloadFile } from "@Services/fileDownload";
import { formCategories, isMergeAllowed } from "@Helpers/constants";
import type { GetFormTemplateResponse } from "@ServerTypes/projects/project.schema";
import type { CreateFolderResponse } from "@ServerTypes/fileSystemServer/fileSystemServer.schema";
import type { ImportExternalVolumeFilesResponse } from "@ServerTypes/fileOperations/fileOperations.schemas";
import { fileFormatNotSupportedToast, UnnamedEstimateNotSupportedToast } from "@Actions/GlobalSearchAction";
import {
  setIsFileVersionUploading,
  setCurrentFileUploadProperties,
  setFileUploadProperties,
  maximiseFileUpload,
  closeFileUpload,
  setFileUploadData,
  emptyFileUploadData,
} from "@Reducers/minimiseFileUpload";
import useFileManagerComponent from "@CubeComponents/FileManager/useFileManagerComponent";
import useFileManagerShared from "@CubeComponents/FileManager/useFileManagerShared";
import {
  useExternalLinks,
  getExternalLinkMoveImpact,
  buildExternalLinkTarget,
  isExternalSharingEnabled,
  type ExternalLinkTarget,
} from "../ExternalLinks";
import * as Sentry from "@Sentry";
import useFormValidator from "../../../../features/forms/components/FormValidation/useFormValidator";
import { getCurrSpaceData } from "@Actions/breadcrumbsdata";
import { setSpaceData } from "@Reducers/breadcrumbsdata";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import type { RootState, AppDispatch } from "@Reducers/index";
import type { TranslatedString } from "@i18n";
import type { FileUploadFormTemplate, FileUploadItem } from "@Reducers/minimiseFileUpload";
// The captured form-template SECTION type; `FileUploadFormTemplate.template` is an array of these.
import type { FormTemplateField as FormTemplateSection } from "@ServerTypes/forms/forms.schema";

/**
 * A dropped/selected browser file. Aliased to the DOM `File` because that is what
 * `CurrentFileUploadPropertiesState.draggedFiles` declares — a narrower local shape would not be
 * assignable to the dispatch payload.
 */
type DroppedFileLike = File;

/**
 * The imperative handle `FileManagerNew` publishes. Declared from the members this hook calls; the
 * component exposes it via `useImperativeHandle`, so there is no shared interface to import.
 */
type FileManagerInstance = {
  dataView: { items?: FileManagerRow[] };
  /** Optional: only the project-mode tree publishes it. */
  navigateToFolder?: (folderId: string) => void;
  getSelectedFilesInFileManager: () => FileManagerRow[];
  refreshFileManager: () => void;
  refreshFiles: () => void;
  setSelectedRows: (rows: (string | number)[]) => void;
  showLoading: () => void;
  hideLoading: () => void;
};

/** One entry of the file-manager context menu / toolbar option map. */
type FileManagerMenuOption = {
  /**
   * Required, both of them: `ContextMenuItem` declares `id` and `name` as non-optional, and every
   * entry in the mapper literal sets both.
   */
  id: string;
  name: string;
  icon?: string;
  onClick?: (...args: never[]) => unknown;
  [key: string]: unknown;
}[];

/** The `{ data: { status, errorMessage } }` envelope every `/fileUpload` and `/fileOperations` call returns. */
type StatusEnvelope = { data: { status?: boolean; errorMessage?: string; [key: string]: unknown } };

/** The Syncfusion `beforeSend` event: the hook mutates it to add visibility flags and reshape `data`. */
type BeforeSendArgs = {
  data?: Record<string, unknown>[];
  targetData?: { spaceId?: ObjectIdString; [key: string]: unknown };
  files?: Record<string, DroppedFileLike>;
  [key: string]: unknown;
};

/** A Syncfusion dropdown/menu selection event. */
type MenuSelectArgs = { item: { id?: string; text?: string }; [key: string]: unknown };

/** The `read` success event; `result.cwd` is the folder that was opened. */
type ReadSuccessArgs = { result: { cwd?: SelectedFileItem; files?: SelectedFileItem[] } };

/** A form-template section as the container/custom-fields flow walks it. */
type FormTemplateSectionLike = { questionFields?: { _id?: string; [key: string]: unknown }[]; [key: string]: unknown };

/** One configured nomenclature field of the current folder. */
type NomenclatureField = { name?: string; value?: string; separator?: string; [key: string]: unknown };

/** A Space as the space picker lists it. */
type SpaceListItem = { _id?: ObjectIdString; spaceName?: string; [key: string]: unknown };

/**
 * A row in the file-manager grid: the canonical Zod item plus the two display fields this hook
 * writes onto a row locally after a rename, which the server item does not declare.
 */
type FileManagerRow = Omit<SelectedFileItem, "permission"> & {
  dateModified?: number;
  modifiedBy?: string;
  /**
   * These are declared REQUIRED here even though `folderItemSchema` marks every field optional.
   * That is deliberate and is a statement about this hook, not about the schema: every code path
   * below dereferences them unconditionally, so the alternative is ~100 non-null assertions saying
   * the same thing one site at a time. If the server ever omits one, the crash is unchanged — this
   * only moves where the assumption is written down.
   */
  id: ObjectIdString;
  name: string;
  parentId: string;
  spaceId: ObjectIdString;
  resourceType: string;
  permission: NonNullable<SelectedFileItem["permission"]>;
  /** Client-only display/selection flags and shortcut back-references not in `folderItemSchema`. */
  isRowDisabled?: boolean;
  answer?: unknown;
  sourceFileVersionId?: ObjectIdString;
  dateCreated?: number;
  [key: string]: unknown;
};

/** The Syncfusion data-provider state: the current working directory and its children. */
type FileManagerData = {
  cwd?: SelectedFileItem;
  files?: FileManagerRow[];
  /** `null` (not absent) after a delete — the reducer path below sets it explicitly. */
  filteredFiles?: FileManagerRow[] | null;
  /** Custom-field columns for the open folder, consumed by `FileManagerNew`'s grid. */
  customFields?: Record<string, unknown>;
  customFieldsByFolder?: Record<string, Record<string, unknown>>;
  /** Set once the project's spaces have resolved; gates the no-access empty state. */
  projectLoaded?: boolean;
};

/**
 * The folder currently open. `isSpaceRoot` is a client-only marker (a Space's top level is not a
 * folder document), and `id` is `"0"` there — the same root sentinel `folderItemSchema` documents.
 */
type CurrentFolder = SelectedFileItem & { isSpaceRoot?: boolean };

// The file manager is always an in-space listing now; the This/External/Reviews tab concept was removed.

interface NavigateLike {
  (location: string, options?: { replace?: boolean; state?: unknown }): void;
}

interface LocationLike {
  pathname: string;
  search: string;
}

interface UseFileManagerProps {
  defaultSelectedTab?: string;
  fileManagerAsPopup?: boolean;
  spaceId: string;
  projectId: string;
  // Project-level ("Folders") surface: show every Space of the project as a tree root. Forced off for
  // popup/attach flows (they must stay single-space).
  projectMode?: boolean;
  showOnlyMergedFile?: boolean;
  setShowProperties?: (update: boolean | ((prev: boolean) => boolean)) => void;
  mobileFileManager?: boolean;
  hideExternalSpace?: boolean;
  hideReviews?: boolean;
  hideAllTabs?: boolean;
  enableSpaceListDropdown?: boolean;
  openThisSpaceByDefault?: boolean;
  createShortcut?: boolean;
  showFolderTree?: boolean;
  handleFileSelectionCallback?: (files: SelectedFileItem[]) => void;
  hideIssues?: boolean;
  hideTasks?: boolean;
  hideEstimates?: boolean;
  hideClashTests?: boolean;
  hideContracts?: boolean;
  hideSchedules?: boolean;
  useAttachedExternalVolume?: boolean;
  hideMergeFiles?: boolean;
  disableContextMenu?: boolean;
  /** Callers map version ids off rows, so entries can be `undefined`. */
  disableFiles?: (string | undefined)[];
  enableOnlyShareableResources?: boolean;
  hideUpload?: boolean;
  enableFilesTypes?: string[] | null;
  hideShortcuts?: boolean;
  handleFolderSelectionCallback?: (folder: SelectedFileItem) => void;
  setSpaceId?: (id: ObjectIdString) => void;
  alreadySelected?: SelectedFileItem[] | null;
  showExcelImport?: boolean;
  defaultPath?: string;
  allowFolderSelectionCallback?: boolean;
  spaceList?: SpaceListItem[];
  disableDragAndDropUpload?: boolean;
  navigate: NavigateLike;
  location: LocationLike;
  nativeMobile?: boolean;
}

type AxiosResponseLike<T> = { data: T };

function useFileManager({
  fileManagerAsPopup = false,
  spaceId,
  projectId,
  projectMode = false,
  showOnlyMergedFile = false,
  setShowProperties,
  mobileFileManager = false,
  hideExternalSpace = false,
  hideReviews = false,
  hideAllTabs = false,
  enableSpaceListDropdown = false,
  createShortcut = false,
  showFolderTree = true,
  handleFileSelectionCallback = () => {},
  hideIssues = false,
  hideTasks = false,
  hideEstimates = false,
  hideClashTests = false,
  hideContracts = false,
  hideSchedules = false,
  useAttachedExternalVolume = false,
  hideMergeFiles = false,
  disableContextMenu = false,
  disableFiles = [],
  enableOnlyShareableResources = false,
  hideUpload = false,
  enableFilesTypes = null,
  hideShortcuts = false,
  handleFolderSelectionCallback = () => {},
  allowFolderSelectionCallback = false,
  setSpaceId = () => {},
  alreadySelected = null,
  showExcelImport = true,
  defaultPath,
  spaceList,
  disableDragAndDropUpload = false,
  navigate,
  location,
  nativeMobile = false,
}: UseFileManagerProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  // Popup/attach flows must never become multi-space, even if a caller passes projectMode by mistake.
  const isProjectMode = projectMode && !fileManagerAsPopup;

  const fileManagerRef = useRef<FileManagerInstance | null>(null);
  // KNOWN BUG, preserved verbatim: this ref is initialised to `{ current: {} }`, so `.current` is an
  // OBJECT WRAPPING another `current` until the first assignment replaces it with the real value.
  // The union states both states rather than hiding the first one.
  // KNOWN BUG, preserved verbatim: initialised to `{ current: {} }` — an object wrapping ANOTHER
  // `current` — rather than to a form template, so `formTemplateRef.current.template` is `undefined`
  // until the first real assignment. The initialiser is asserted via an intersection that includes
  // its own type, so it is a single erased `as` and the emitted value is unchanged.
  const formTemplateRef = useRef<FileUploadFormTemplate>({ current: {} } as {
    current: Record<string, unknown>;
  } & FileUploadFormTemplate);
  const formValidator: ReturnType<typeof useFormValidator> = useFormValidator({});
  // KNOWN BUG, preserved verbatim: same wrapping-initialiser shape as `formTemplateRef` above, so
  // `.length` reads `undefined` until the first assignment.
  const fileUploadListRef = useRef<FileUploadItem[] | { current: unknown[] }>({ current: [] });
  const nomenclatureRef = useRef<string[]>([]);
  const spaceListRef = useRef<SpaceListItem[] | null>(null);
  const tabRef = useRef<Record<string, unknown>>({});
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // State
  const [fileManagerData, setFileManagerData] = useState<FileManagerData>({
    cwd: {},
    files: [],
  });
  const [showContainerImportModal, setShowContainerImportModal] = useState<boolean>(false);
  const [importMode, setImportMode] = useState<"container" | "file" | null>(null);
  // KNOWN BUG, preserved verbatim: `FileManagerNew`'s two close handlers call
  // `setOpenRenameModal(false)`, replacing the whole object with a boolean. It only works because
  // the render guard reads `openRenameModal.state`, and `(false).state` is `undefined` — falsy — so
  // the modal closes and `oldName` is silently dropped. The union states both shapes.
  const [openRenameModal, setOpenRenameModal] = useState<{ state: boolean; oldName: string } | boolean>({
    state: false,
    oldName: "",
  });
  // KNOWN BUG, preserved verbatim: `handleFileTransmit` assigns the whole SELECTION ARRAY here while
  // the rename flow reads `selectedFile.id` — so after a transmit, `.id` is `undefined`. The union
  // states both shapes rather than hiding one of them.
  const [selectedFile, setSelectedFile] = useState<Partial<FileManagerRow> | FileManagerRow[]>({});
  const [newName, setNewName] = useState<string>("");
  const [folderName, setFolderName] = useState<string>("");
  const [createReviewModal, setCreateReviewModal] = useState<{ show: boolean }>({ show: false });
  const [fileAllowedExtensions, setFileAllowedExtensions] = useState<{ [k: string]: boolean | number | string }>({});
  const [openMergeModal, setOpenMergeModal] = useState<boolean>(false);
  const [openMergeEstimateModal, setOpenMergeEstimateModal] = useState<boolean>(false);
  const [openFolderCreationModal, setOpenFolderCreationModal] = useState<boolean>(false);
  const [openFileImport, setOpenFileImport] = useState<boolean>(false);
  const [openContainerModal, setOpenContainerModal] = useState<boolean>(false);
  const [currentFolder, setCurrentFolder] = useState<CurrentFolder>({ id: "0" as ObjectIdString });
  // Guards the #folderPath hash-writer from firing on mount: the initial currentFolder ({id:"0"}) would
  // otherwise clear/overwrite a deep-link hash before a browser-refresh restore can read it.
  const hashWriterInitRef = useRef(false);
  const [selectedFilesToDelete, setSelectedFilesToDelete] = useState<FileManagerRow[] | null>(null);
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState<boolean>(false);
  const [openCreateTransmittalModal, setOpenCreateTransmittalModal] = useState<boolean>(false);
  const [filesToCopy, setFilesToCopy] = useState<FileManagerRow[] | null>(null);
  const [filesToCut, setFilesToCut] = useState<FileManagerRow[] | null>(null);
  const [formTemplateReceived, setFormTemplateReceived] = useState<Record<string, unknown> | boolean>({});
  const [containerFormTemplate, setContainerFormTemplate] = useState<FormTemplateSection[]>([]);
  const [fileTypeValue, setFileTypeValue] = useState<string | null>(null);
  const [resourceType, setResourceType] = useState<Record<string, unknown>>({});
  const [containerName, setContainerName] = useState<string>("");
  const [nomenclatureValues, setNomenclatureValues] = useState<string[]>([]);
  const [draggedFiles, setDraggedFiles] = useState<DroppedFileLike[] | null>([]);
  const [path, setPath] = useState<string | null>(null);
  const [preSearchText, setPreSearchText] = useState<string | null>("");
  const [toolBarBtnState, setToolBarBtnState] = useState<{
    upload: boolean;
    refresh: boolean;
    rename: boolean;
    delete: boolean;
    download: boolean;
    details: boolean;
  }>({
    upload: !hideUpload,
    refresh: true,
    rename: false,
    delete: false,
    download: false,
    details: true,
  });
  // Always the in-space listing now — no tab state / tab switching.
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [importSubmenuAnchor, setImportSubmenuAnchor] = useState<null | HTMLElement>(null);

  // Redux state — selected individually so missing slices (e.g. on mobile) don't break the hook
  const layout = useSelector((state: RootState) => state.layout) ?? {};
  const importOnlyUploads = !!layout?.FILE_MANAGER?.IMPORT_ONLY_UPLOADS;
  const getSpaceToggle = useSelector((state: RootState) => state.getSpaceToggle);
  const projectData = useSelector((state: RootState) => state.projectData);
  const userDetails = useSelector((state: RootState) => state.userDetails);

  const fileUploadList = useSelector((state: RootState) => state.fileUploadList) ?? [];
  const isFileVersionUploading = useSelector((state: RootState) => state.isFileVersionUploading);
  const fileUploadState = useSelector((state: RootState) => state.fileUploadState);
  const spaceData = useSelector((state: RootState) => state.spaceData);
  const refreshFileManager = useSelector((state: RootState) => state.refreshFileManager);

  const searchParams = new URLSearchParams(location.search);
  const fileSearchText = searchParams.get("searchText");
  // Project ("Folders") mode carries the pre-search's target Space here (see getProjectFoldersPageUrl):
  // the search is Space-specific, so we scope it to this id when a folder hasn't been focused yet
  // (e.g. the pre-search fires before the deep-link Space focus completes).
  const searchSpaceId = searchParams.get("spaceId");

  const open = Boolean(anchorEl);
  const importSubmenuOpen = Boolean(importSubmenuAnchor);

  // Handler functions
  const showToast = () => {
    dispatch(
      showToastNotification({
        content: t("Insufficient permission to perform this action."),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const handleDeleteClick = (selectedFiles: FileManagerRow[]) => {
    let isUserAllowedToDeleteAllFiles = true;
    for (const eachObj of selectedFiles) {
      if (!eachObj?.permission?.delete) {
        isUserAllowedToDeleteAllFiles = false;
      }
    }
    if (isUserAllowedToDeleteAllFiles) {
      setSelectedFilesToDelete(selectedFiles);
      setOpenDeleteConfirmationModal(true);
    } else {
      showToast();
    }
  };

  useEffect(() => {
    // In project mode there is no single "current space" to hydrate; the tree sources spaces itself.
    if (spaceId && !isProjectMode) {
      // BLOCKED ORIGIN: `UseFileManagerProps.spaceId` is plain `string` because it comes from
      // `FileManagerNewProps.spaceId`, which is fed by many callers outside this feature. Brand that
      // chain and this assertion can be deleted.
      dispatch(getCurrSpaceData({ id: spaceId as ObjectIdString }));
    }
  }, [spaceId, isProjectMode]);

  const handleDeleteModalClose = () => {
    setOpenDeleteConfirmationModal(false);
    setSelectedFilesToDelete(null);
  };

  const handleDeleteSuccess = (status: boolean) => {
    if (status) {
      handleDeleteModalClose();
      const deletedIds = selectedFilesToDelete!.map((file: FileManagerRow) => file.id);
      setFileManagerData(
        (prev: FileManagerData): FileManagerData => ({
          ...prev,
          files: prev.files?.filter((file: FileManagerRow) => !deletedIds.includes(file.id)),
          filteredFiles: prev.filteredFiles
            ? prev.filteredFiles.filter((file: FileManagerRow) => !deletedIds.includes(file.id))
            : null,
        }),
      );
      handleUnSelectAllOption();
      setToolBarBtnState(() => ({
        upload: !hideUpload,
        refresh: true,
        rename: false,
        delete: false,
        download: false,
        details: true,
      }));
      setSelectedFilesToDelete(null);
    }
  };

  const getSelectedFilesInFileManager = () => {
    return fileManagerRef.current?.getSelectedFilesInFileManager() || [];
  };

  const handleRenameFile = (selectedFiles: FileManagerRow[]) => {
    const file = selectedFiles[0] ?? getSelectedFilesInFileManager()[0];
    if (!file?.permission?.write) {
      showToast();
    } else {
      setNewName(file.name);
      setOpenRenameModal({ state: true, oldName: file.name });
      setSelectedFile(file);
    }
  };

  const handleFileTransmit = (selectedFiles: FileManagerRow[]) => {
    const allFilesHaveSharePermission = selectedFiles.every((file: FileManagerRow) => file.permission.share);
    if (allFilesHaveSharePermission) {
      setSelectedFile(selectedFiles);
      setOpenCreateTransmittalModal(true);
    } else {
      showToast();
    }
  };

  const fetchConstituentFilesOfMergedFile = async (fileVersion: string) => {
    const mergedFile = (await axiosWrapper.post<GetFileVersionDataResponse>(
      `${getBackendUrl()}/fileOperations/getFileVersionData`,
      {
        fileVersionId: fileVersion,
      },
    )) as AxiosResponseLike<GetFileVersionDataResponse>;
    const constituentFiles = mergedFile.data.data?.sourceFileVersionId ?? [];
    if (constituentFiles.length === 0) {
      dispatch(
        showToastNotification({
          content: t("This merged file does not contain any files. Kindly add files to view."),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return false;
    }
    return true;
  };

  const handleLocationOpen = (location: string, args?: FileManagerRow) => {
    if (!fileManagerAsPopup) {
      if (_.get(args, "resourceType") && _.get(args, "resourceType") != "RESOURCE_TYPE_FOLDER" && mobileFileManager) {
        (
          window as Window & { ReactNativeWebView?: { postMessage: (m: string) => void } }
        )?.ReactNativeWebView?.postMessage(JSON.stringify({ action: "disableFileOpen", location }));
      } else {
        navigate(location);
      }
    }
  };

  const handleOpenOrPreloadAndOpen = async (selectedFiles: FileManagerRow[], id: string) => {
    if (id === "open" && ["kanban"].includes(selectedFiles[0]!.resourceType)) {
      handleLocationOpen("/kanban");
    }
    const fileId = resolveFileId(selectedFiles[0]!);
    if (selectedFiles[0]!.resourceType === "RESOURCE_TYPE_FILE" && !fileId) {
      dispatch(
        showToastNotification({
          content: t("No file associated to open"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (
      ["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FILE", "RESOURCE_TYPE_ISSUE", "RESOURCE_TYPE_EMBED"].includes(
        selectedFiles[0]!.resourceType,
      )
    ) {
      if (selectedFiles[0]!.resourceType === "RESOURCE_TYPE_MERGED_FILE") {
        const result = await fetchConstituentFilesOfMergedFile(selectedFiles[0]!.fileVersion!);
        if (!result) {
          return;
        }
      }
      if (selectedFiles[0]!.resourceType === "RESOURCE_TYPE_ISSUE") {
        const locationUrl: string = getViewerUrl({
          fileVersionId: selectedFiles[0]!.fileId,
          issueId: selectedFiles[0]!.snapshot!.issueId,
          projectId,
        })!;
        handleLocationOpen(locationUrl);
        return;
      }
      const viewerMode =
        id === "preloadAndOpen" ? "scs" : id === "loadWithoutProperties" ? "skipProperties" : undefined;
      const targetUrl: string = buildViewerUrl(selectedFiles[0]!, fileId!, viewerMode)!;
      if (canOpenViewer(selectedFiles[0]!)) {
        handleLocationOpen(targetUrl);
      } else {
        showToast();
      }
    }
  };

  const handleDownloadFile = (selectedFiles: FileManagerRow[]) => {
    const files = selectedFiles;
    const fileVersionIds: string[] = [];
    const folderIds: string[] = [];
    let isDownloadAllowed = true;
    files.forEach((item: FileManagerRow) => {
      const fileExtension = item.name.split(".").pop();
      item.isFile ? fileVersionIds.push(item.fileVersion!) : folderIds.push(item._id!);
      isDownloadAllowed = (item.permission?.download && isDownloadAllowed) as boolean;
      if (fileExtension === PDF_TYPE[0]) {
        return;
      }
    });
    if (isDownloadAllowed) {
      downloadFile({
        fileVersionIds,
        folderIds,
        type: "Download",
        mobileFileManager,
        fileName: _.get(files, "[0].name"),
      });
    } else {
      showToast();
    }
  };

  // Export a DWG/DXF drawing to PDF via the cad2pdf microservice and download the result.
  const handleExportToPdf = (selectedFiles: FileManagerRow[]) => {
    const file = selectedFiles?.[0];
    if (!file) return;
    const ext = (file.name?.split(".").pop() || "").toLowerCase();
    if (!["dwg", "dxf"].includes(ext)) {
      dispatch(
        showToastNotification({
          content: t("Only DWG/DXF files can be exported to PDF."),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (file.permission && file.permission.download === false) {
      showToast();
      return;
    }
    const url = new URL(`${getBackendUrl()}/cad2pdf/download`);
    url.searchParams.set("fileVersionId", file.fileVersion!);
    url.searchParams.set("fileName", file.name);
    dispatch(showToastNotification({ content: t("Preparing PDF…"), type: TOAST_TYPE_SUCCESS }));
    axiosWrapper
      .get<Blob>(url.toString(), { responseType: "blob" })
      .then((response: { data?: Blob }) => {
        const blob = response.data;
        // Backend returns application/pdf on success; anything else means the export failed.
        if (blob && blob.type && blob.type.indexOf("pdf") === -1) {
          throw new Error("PDF export failed");
        }
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob!);
        link.download = `${file.name.replace(/\.[^.]+$/, "")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch((error: unknown) => {
        console.error("[cad2pdf] export failed:", error);
        dispatch(
          showToastNotification({
            content: t("PDF export failed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      });
  };

  const getPermission = () => {
    const value = currentFolder || {};
    if (Object.hasOwn(value, "permission")) {
      return value.permission!.upload;
    }
    return false;
  };

  const isFileItem = (name: string) => name?.includes(".");

  const processDroppedFiles = (sortFilesList: DroppedFileLike[]): boolean => {
    if (importOnlyUploads) return false;
    if (!sortFilesList.length) return false;
    if (!getPermission()) {
      showToast();
      return false;
    }

    const firstIsFile = isFileItem(sortFilesList[0]?.name!);
    if (!firstIsFile) {
      dispatch(showToastNotification({ content: t("Folder cannot be dragged"), type: TOAST_TYPE_ERROR }));
      return false;
    }

    const notContainFolder = sortFilesList.some((f: DroppedFileLike) => isFileItem(f?.name));
    if (!notContainFolder) return false;

    setDraggedFiles(sortFilesList);

    if (fileUploadState === "MINIMISED") {
      dispatch(
        showToastNotification({
          content: t("Please close other upload operations before proceeding!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return false;
    }

    dispatch(maximiseFileUpload());
    dispatch(
      setCurrentFileUploadProperties({
        formTemplate: formTemplateRef.current,
        currentFolderRef: currentFolder,
        isUploadAllowed: getPermission(),
        fileNomenclature: _.get(currentFolder, "fileNomenclature") as string | null,
        workspaceId: currentFolder?.spaceId ?? spaceId,
        hierarchyId: currentFolder.hierarchyId,
      }),
    );

    const currentFiles = fileManagerData?.files?.filter((f: FileManagerRow) => f.parentId == currentFolder.id) || [];
    const processed = processFileList({
      fileList: [...sortFilesList],
      currentFiles,
      fileNomenclature: _.get(currentFolder, "fileNomenclature") as Parameters<
        typeof processFileList
      >[0]["fileNomenclature"],
      isFileVersionUploading,
      uploadFolderData: {},
      uploadSpaceId: currentFolder?.spaceId ?? spaceId,
      currentFolder,
    });

    dispatch(setFileUploadData(processed));
    return true;
  };

  const handleEmptyStateDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (disableDragAndDropUpload) return;
    if (!getPermission()) {
      showToast();
      return;
    }
    const files = Array.from(event.dataTransfer?.files || []);
    processDroppedFiles(files);
  };

  const handleEmptyStateDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const beforeSending = (args: BeforeSendArgs) => {
    const setVisibilityFlags = () => {
      if (hideIssues) args.hideIssues = true;
      if (hideTasks) args.hideTasks = true;
      if (hideEstimates) args.hideEstimates = true;
      if (hideClashTests) args.hideClashTests = true;
      if (hideSchedules) args.hideSchedules = true;
      if (hideContracts) args.hideContracts = true;
      if (hideMergeFiles) args.hideMergeFiles = true;
      if (hideShortcuts) args.hideShortcuts = true;
      if (useAttachedExternalVolume) args.useAttachedExternalVolume = true;
      args.showOnlyMergedFile = !!showOnlyMergedFile;
    };

    const ensureDataArray = () => {
      // Project mode spans many spaces: derive the space from the datum itself or the current node,
      // never a single ambient prop. Single-space mode reduces to the old behavior (currentFolder has no
      // spaceId at the root, so it falls back to the prop; folder nodes carry the same spaceId).
      // Before any folder is focused, project mode has no ambient space; fall back to the URL's search
      // Space (searchSpaceId) so a pre-search that fires before the deep-link focus still scopes correctly.
      const opSpaceId = isProjectMode ? (searchSpaceId ?? currentFolder?.spaceId) : spaceId;
      if (Array.isArray(args.data) && args.data.length) {
        args.data = args.data.map((d: Record<string, unknown>) => ({
          ...d,
          spaceId: d.spaceId ?? opSpaceId,
        }));
        return;
      }
      args.data = [{ spaceId: opSpaceId }];
    };

    const handleUpload = () => {
      const sortFilesList: DroppedFileLike[] = Object.values(args.files || {});
      processDroppedFiles(sortFilesList);
      args.cancel = true;
    };

    const handleCopy = () => {
      // Stamp the paste target's Space (the destination folder's) so the server can reject a cross-Space
      // paste (pasting is only allowed within the same Space).
      args.targetData!.spaceId ||= (currentFolder?.spaceId ?? spaceId) as ObjectIdString;

      const invalid =
        !Array.isArray(args.data) ||
        args.data.some((obj: Record<string, unknown>) => !Object.hasOwn(obj, "fileVersion"));

      if (invalid) {
        args.cancel = true;
        dispatch(showToastNotification({ content: t("Container cannot be copy pasted"), type: TOAST_TYPE_ERROR }));
      }
    };

    switch (args.action) {
      case "upload":
        handleUpload();
        return;
      case "copy":
        handleCopy();
        break;
      case "move":
        // Same as copy: stamp the target Space so the server can reject a cross-Space move.
        if (args.targetData) args.targetData.spaceId ||= (currentFolder?.spaceId ?? spaceId) as ObjectIdString;
        break;
      case "read":
        setDraggedFiles(null);
        break;
      default:
        break;
    }

    ensureDataArray();
    setVisibilityFlags();
  };

  const handleResourceCut = (selectedFiles: FileManagerRow[]) => {
    setFilesToCut(selectedFiles);
    setFilesToCopy(null);
  };

  const handleResourceCopy = (selectedFiles: FileManagerRow[]) => {
    let userAllowedToCopyAllFiles = true;
    selectedFiles?.map((file: FileManagerRow) => {
      if (!file.permission?.copy) {
        userAllowedToCopyAllFiles = false;
      }
    });
    if (userAllowedToCopyAllFiles) {
      setFilesToCopy(selectedFiles);
      setFilesToCut(null);
    } else {
      showToast();
    }
  };

  const performResourcePaste = async (selectedFolder: FileManagerRow[] | null = null) => {
    if (selectedFolder && selectedFolder.length > 1) {
      dispatch(
        showToastNotification({
          content: t("Select one folder to paste content to"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (!filesToCopy && !filesToCut) {
      dispatch(
        showToastNotification({
          content: t("No resource selected to paste"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    // Erased: `selectedFolder` is null-checked by the early return above, and `[0]` under
    // `noUncheckedIndexedAccess` is `| undefined` — which the `??` already handles.
    const destinationFolder = selectedFolder![0] ?? currentFolder;
    const isuserAllowedToPaste = destinationFolder?.permission?.write;
    if (!isuserAllowedToPaste) {
      showToast();
      return;
    }
    let actionFiles: FileManagerRow[] | undefined;
    if (filesToCopy) {
      actionFiles = filesToCopy;
    } else if (filesToCut) {
      actionFiles = filesToCut;
    }
    // Guard: pasting is only allowed within the same Space (the paste icon is also disabled for this in the
    // context menu; this covers any other entry path). Fires only when both Spaces are known and differ.
    const clipboardSpaceId = actionFiles?.[0]?.spaceId;
    const destinationSpaceId = destinationFolder?.spaceId;
    if (clipboardSpaceId && destinationSpaceId && String(clipboardSpaceId) !== String(destinationSpaceId)) {
      dispatch(
        showToastNotification({
          content: t("Files can't be pasted into a different Space."),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (actionFiles) {
      const allParams: Record<string, unknown> = {
        action: filesToCopy ? "copy" : "move",
        data: actionFiles,
        targetData: destinationFolder,
        renameFiles: [],
      };
      beforeSending(allParams);
      if (!allParams.cancel) {
        fileManagerRef.current?.showLoading();
        const fsUrl = `${getBackendUrl()}/fileSystemServer/${allParams.action === "copy" ? "copyFiles" : "moveFiles"}`;
        const res = await axiosWrapper.post<CreateFolderResponse>(fsUrl, {
          ...allParams,
        });

        if (!res.data.status) {
          dispatch(
            showToastNotification({
              content: untranslated(res.data?.errorMessage || res.data?.error?.code || "") || SOMETHING_WENT_WRONG,
              type: TOAST_TYPE_ERROR,
            }),
          );
          fileManagerRef.current?.hideLoading();
        } else {
          refreshCurrentFolder();
        }
        setFilesToCopy(null);
        setFilesToCut(null);
      }
    }
  };

  /**
   * Pending cut/paste held while the "this will break external links" warning is on screen.
   *
   * The destination is captured here rather than re-read on confirm: the user could click elsewhere
   * in the grid while the dialog is open, and the move must land where they aimed it.
   */
  const [pendingMoveWarning, setPendingMoveWarning] = useState<{
    selectedFolder: FileManagerRow[] | null;
    linkCount: number;
    itemCount: number;
  } | null>(null);

  /**
   * Cut/paste entry point (CP-283, FR-24).
   *
   * A COPY never breaks anything — duplicating a linked item does not carry the link over — so only
   * a cut is checked. If the items being moved carry active links of their own, the user is warned
   * and can cancel before the move happens; otherwise the paste proceeds unchanged.
   */
  const handleResourcePaste = async (selectedFolder: FileManagerRow[] | null = null) => {
    if (filesToCut?.length) {
      const resourceIds = filesToCut.map((file) => String(file.id ?? file.hierarchyId)).filter(Boolean);
      if (resourceIds.length) {
        const impact = await getExternalLinkMoveImpact(resourceIds).catch((error: unknown) => {
          // Reported, then the paste proceeds unwarned. Blocking a legitimate move because the
          // impact check failed would be worse than missing the warning, which the server-side
          // invalidation still handles correctly.
          Sentry.captureException(error);
          return null;
        });
        if (impact?.status && (impact.affectedLinkCount ?? 0) > 0) {
          setPendingMoveWarning({
            selectedFolder,
            linkCount: impact.affectedLinkCount ?? 0,
            itemCount: impact.affectedResourceCount ?? 0,
          });
          return;
        }
      }
    }
    await performResourcePaste(selectedFolder);
  };

  const confirmPendingMove = async () => {
    const pending = pendingMoveWarning;
    setPendingMoveWarning(null);
    if (pending) await performResourcePaste(pending.selectedFolder);
  };

  const handleSelectAllOption = () => {
    const getCondition = (file: FileManagerRow) => {
      if (!currentFolder.resourceType || currentFolder.resourceType == "RESOURCE_TYPE_TRANSMITTAL") {
        return file.transmittalVersionId == currentFolder.id;
      } else if (currentFolder.resourceType == "RESOURCE_TYPE_FOLDER") {
        return file.parentId == currentFolder.id;
      }
      return false;
    };
    const currentFiles = fileManagerData.files?.filter((file: FileManagerRow) => getCondition(file));
    const allRowIndexes = [...Array(currentFiles!.length).keys()];
    fileManagerRef.current?.setSelectedRows(allRowIndexes);
  };

  const handleUnSelectAllOption = () => {
    fileManagerRef.current?.setSelectedRows([]);
  };

  const reloadFileManager = () => {
    if (fileManagerRef.current) {
      fileManagerRef.current?.refreshFileManager();
    }
  };

  const refreshCurrentFolder = () => {
    if (fileManagerRef.current) {
      fileManagerRef.current?.refreshFiles();
    }
  };

  const handleSpaceSelect = (args: { isInteracted?: boolean; value?: ObjectIdString }) => {
    if (args.isInteracted) {
      setSpaceId(args.value!);
    }
  };

  const handleSpaceToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (spaceId) {
      await dispatch(
        setSpaceToggle({
          spaceId,
          toggle: event.target.checked,
        }),
      );
      if (fileManagerRef.current) {
        refreshCurrentFolder();
      }
    }
  };

  const handleDetailIconClick = () => {
    const selectedFile = getSelectedFilesInFileManager();

    if (mobileFileManager) {
      (
        window as Window & { ReactNativeWebView?: { postMessage: (m: string) => void } }
      )?.ReactNativeWebView?.postMessage(
        JSON.stringify({
          action: "properties",
          data: selectedFile[0],
        }),
      );
    } else if (setShowProperties) {
      setShowProperties((prev: boolean) => !prev);
    }
  };

  const getFormTemplate = async () => {
    if (projectId && currentFolder.hierarchyId) {
      const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
        projectId,
        hierarchyId: currentFolder.hierarchyId,
        resourceType: formCategories.files,
      });

      if (response.data.status && response.data.data) {
        const template = response.data?.data?.template || [];
        // `getFormTemplateResponse.data.template` is `z.array(z.unknown())` on the server, so the
        // section shape is asserted here. Tightening that schema is the real fix.
        setContainerFormTemplate(template as FormTemplateSection[]);
        formTemplateRef.current = {
          template: template as FormTemplateSection[],
          formTemplateId: response.data.data._id,
        };
      }
      setFormTemplateReceived((prev) => ({
        ...(prev as Record<string, boolean>),
        [currentFolder.hierarchyId!]: true,
      }));
    }
  };

  const handleFileAndFolderUpload = (args: MenuSelectArgs) => {
    setFormTemplateReceived(false);
    // Upload targets the current folder's space (project mode spans many); single-space falls back to prop.
    const uploadSpaceId = currentFolder?.spaceId ?? spaceId;
    if (fileUploadState == "CLOSED") {
      dispatch(emptyFileUploadData());
    }
    if (
      !(fileUploadListRef.current as FileUploadItem[]).length ||
      ((fileUploadListRef.current as FileUploadItem[])[0]?.hierarchyId == currentFolder.hierarchyId &&
        (fileUploadListRef.current as FileUploadItem[])[0]?.spaceId == uploadSpaceId)
    ) {
      if (!(fileUploadListRef.current as FileUploadItem[]).length) {
        getFormTemplate();
      }
      dispatch(
        setFileUploadProperties({
          [`${uploadSpaceId}_${currentFolder.hierarchyId}`]: {
            currentFiles:
              (fileManagerData?.files?.filter(
                (file: FileManagerRow) => file.parentId == currentFolder.id,
              ) as unknown[] as FileUploadItem[]) || [],
            workspaceId: uploadSpaceId,
            fileNomenclature: _.get(currentFolder, "fileNomenclature", "") as { toString(): string } as string | null,
            isUploadAllowed: getPermission(),
            formTemplate: formTemplateRef.current,
          },
        }),
      );
      dispatch(
        setCurrentFileUploadProperties({
          currentFiles:
            (fileManagerData?.files?.filter(
              (file: FileManagerRow) => file.parentId == currentFolder.id,
            ) as unknown[] as FileUploadItem[]) || [],
          workspaceId: uploadSpaceId,
          hierarchyId: currentFolder.hierarchyId,
          fileAllowedExtensions,
          draggedFiles: draggedFiles as File[] | undefined,
          isFolderUpload: args.item.id === "folderUpload",
          fileNomenclature: _.get(currentFolder, "fileNomenclature", "") as string | null,
          isUploadAllowed: getPermission(),
          formTemplate: formTemplateRef.current,
          formTemplateReceived: formTemplateReceived as boolean | undefined,
          fileUploadStateRef: { current: "MAXIMISED" },
          currentFolderRef: currentFolder,
        }),
      );
      dispatch(maximiseFileUpload());
    } else {
      dispatch(
        showToastNotification({
          content: t("Another upload is in progress. Please close that popup to proceed."),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleSelectOption = (args: MenuSelectArgs) => {
    if ((["fileUpload", "folderUpload"] as (string | undefined)[]).includes(args.item.id)) {
      if (args.item.id === "folderUpload" && !getPermission()) {
        showToast();
        return;
      }
      handleFileAndFolderUpload(args);
    } else if (args.item.id == "merge") {
      setOpenMergeModal(true);
    } else if (args.item.id == "newFolder") {
      setOpenFolderCreationModal(true);
    } else if (args.item.id == "container") {
      refreshCurrentFolder();
      setOpenContainerModal(true);
    } else if (args.item.id == "fileImport") {
      setOpenFileImport(true);
    } else if (args.item.id == "importContainer") {
      setImportMode("container");
      setShowContainerImportModal(true);
    } else if (args.item.id == "importFile") {
      setImportMode("file");
      setShowContainerImportModal(true);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const permissionCheck = getPermission();
    if (permissionCheck) {
      setAnchorEl(event.currentTarget);
    } else {
      showToast();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setImportSubmenuAnchor(null);
  };

  const handleImportSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setImportSubmenuAnchor(event.currentTarget);
  };

  const handleImportSubmenuClose = () => {
    setImportSubmenuAnchor(null);
  };

  const closeReviewModal = () => {
    setCreateReviewModal({ show: false });
  };

  const onFileSelect = (args: FileManagerRow[]) => {
    const fileList = args;
    let fileCount = 0;
    let estimateCount = 0;
    let scheduleCount = 0;
    let contractCount = 0;
    const toolBtnState = {
      rename: fileList.length == 1,
      delete: fileList.length > 0,
      download: fileList.length > 0,
    };
    fileList.forEach((file: FileManagerRow) => {
      const resourceType = file.resourceType;

      if (resourceType == "RESOURCE_TYPE_ESTIMATE") {
        estimateCount = 1;
      } else if (resourceType == "RESOURCE_TYPE_SCHEDULE") {
        scheduleCount = 1;
      } else if (resourceType == "RESOURCE_TYPE_CONTRACT") {
        contractCount = 1;
      } else {
        if (file.isEmptyContainer || resourceType == "RESOURCE_TYPE_EMBED") {
          toolBtnState.download = false;
        }
        fileCount = 1;
      }
    });
    if (!fileManagerAsPopup) {
      if (fileList.length == 0) {
        dispatch(selectedFilesFileManager([]));
      } else {
        dispatch(selectedFilesFileManager(args));
      }
    }

    if (fileCount + estimateCount + scheduleCount > 1) {
      toolBtnState.delete = false;
    }
    if (estimateCount || scheduleCount || contractCount) {
      toolBtnState.download = false;
    }

    setToolBarBtnState((prev) => ({
      ...prev,
      ...toolBtnState,
    }));

    if (typeof handleFileSelectionCallback === "function") {
      handleFileSelectionCallback(args);
    }
  };

  const isShortcutType = (resourceTypeStr: string) =>
    resourceTypeStr === "RESOURCE_TYPE_SHORTCUT_FILE" || resourceTypeStr === "RESOURCE_TYPE_SHORTCUT_MERGED_FILE";

  const handleDirectNav = (row: FileManagerRow) => {
    switch (row.resourceType) {
      case "RESOURCE_TYPE_ESTIMATE": {
        handleLocationOpen(getEstimateUrl({ projectId, estimateId: row.estimateId }), row);
        return true;
      }
      case "RESOURCE_TYPE_SCHEDULE": {
        handleLocationOpen(getScheduleUrl({ projectId, scheduleId: row.schId }), row);
        return true;
      }
      case "RESOURCE_TYPE_CONTRACT": {
        handleLocationOpen(
          getContractDetailUrl({ projectId, contractId: row.contractId, selectedTab: "details" }),
          row,
        );
        return true;
      }
      default:
        return false;
    }
  };

  const ensureFileEngineOk = (row: FileManagerRow) => {
    if (row.resourceType !== "RESOURCE_TYPE_FILE") return true;
    if (!row.engineType) {
      dispatch(
        showToastNotification({
          content: t("Select an engine to view the file"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return false;
    }
    if (row.engineType === PDF_ENGINE) {
      dispatch(setViewerState({ isSidebarOpen: false }));
    }
    return true;
  };

  const resolveFileId = (row: FileManagerRow) => {
    if (row.resourceType === "RESOURCE_TYPE_ISSUE") {
      return row.fileVersion || row.fileId;
    }
    if (isShortcutType(row.resourceType)) {
      return row.sourceFileVersionId;
    }
    return row.fileVersion;
  };

  const buildViewerUrl = (row: FileManagerRow, fileId: string, mode?: "scs" | "skipProperties") => {
    const shortcutParentId = row.parentId === row.spaceId ? "0" : row.parentId;
    const base: Parameters<typeof getViewerUrl>[0] = {
      fileVersionId: fileId,
      spaceId: row.spaceId,
      projectId,
      shortcutParentId,
    };
    if (row.resourceType === "RESOURCE_TYPE_ISSUE") base.issueId = row.issueId as string;
    if (mode) base.mode = mode;
    return getViewerUrl(base);
  };

  const canOpenViewer = (row: FileManagerRow) => row?.permission?.read || row.resourceType !== "RESOURCE_TYPE_FILE";

  const onFileClick = async (clickedRow: FileManagerRow) => {
    if (!clickedRow?.isFile || fileManagerAsPopup) return;

    if (handleDirectNav(clickedRow)) return;

    if (clickedRow.resourceType === "RESOURCE_TYPE_MERGED_FILE") {
      const ok = await fetchConstituentFilesOfMergedFile(clickedRow.fileVersion!);
      if (!ok) return;
    }

    if (!ensureFileEngineOk(clickedRow)) return;

    const fileId = resolveFileId(clickedRow);
    if (clickedRow.resourceType === "RESOURCE_TYPE_FILE" && !fileId) {
      dispatch(
        showToastNotification({
          content: t("No file associated to open"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }

    const targetUrl: string = buildViewerUrl(clickedRow, fileId!)!;

    if (canOpenViewer(clickedRow)) {
      handleLocationOpen(targetUrl, clickedRow);
    } else {
      showToast();
    }
  };

  const toolbarClick = (args: unknown, id: string) => {
    const selectedFiles = getSelectedFilesInFileManager();

    if (id == "refresh") {
      refreshCurrentFolder();
    } else if (id == "delete") {
      handleDeleteClick(selectedFiles);
    } else if (id == "rename") {
      handleRenameFile(selectedFiles);
    } else if (id == "download") {
      handleDownloadFile(selectedFiles);
    } else if (id === "properties") {
      handleDetailIconClick();
    }
  };

  const onSuccess = (args: ReadSuccessArgs) => {
    if ((createShortcut || allowFolderSelectionCallback) && handleFolderSelectionCallback) {
      handleFolderSelectionCallback({
        ...args.result.cwd,
      });
    }
  };

  const saveFileMergeModal = async (listOfSelectedFiles: Record<string, FileManagerRow>, mergeFileName: string) => {
    const sourceFileVersionId = Object.values(listOfSelectedFiles).map((val: FileManagerRow) => {
      if (val.isEmptyContainer) {
        return val.id;
      }
      return val.fileVersion;
    });
    const fileObjArray = Object.values(listOfSelectedFiles);
    const fileSettings: ReturnType<typeof isMergeAllowed> = isMergeAllowed(fileObjArray);

    const mergeParams: Record<string, unknown> = {
      sourceFileVersionId,
      originalName: mergeFileName,
      parentId: currentFolder.hierarchyId,
      spaceId: currentFolder?.spaceId ?? spaceId,
      folderType: currentFolder.folderType ?? 0,
    };
    if (!fileSettings.mergedAllowed) {
      dispatch(fileFormatNotSupportedToast());
    } else if (fileSettings.isFile) {
      if (!currentFolder?.permission?.upload) {
        showToast();
        return;
      }
      const response = (await createMergeFile(mergeParams)) as StatusEnvelope;
      if (response.data.status) {
        if (!fileManagerAsPopup) {
          dispatch(selectedFilesFileManager([]));
        }
        if (typeof handleFileSelectionCallback === "function") {
          handleFileSelectionCallback([]);
        }
        closeFileMergeModal();
        refreshCurrentFolder();
        dispatch(
          showToastNotification({
            content: t("Files merged successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: response.data.errorMessage as TranslatedString,
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    } else if (!fileSettings.isFile) {
      await createMergeEstimateModal(listOfSelectedFiles, mergeFileName);
    }
  };

  const closeFileMergeModal = () => {
    setOpenMergeModal(false);
  };

  const closeFileImportPopup = () => {
    setOpenFileImport(false);
  };

  const closeMergeEstimateModal = () => {
    setOpenMergeEstimateModal(false);
  };

  const handleFileImportCallback = async (listOfSelectedFiles: Record<string, FileManagerRow>) => {
    closeFileImportPopup();
    if (Object.keys(listOfSelectedFiles).length) {
      const payload: ImportExternalVolumeFilesBody = {
        files: Object.keys(listOfSelectedFiles),
        spaceId: currentFolder?.spaceId ?? spaceId,
        parentId: currentFolder._id,
      };
      const response = (await axiosWrapper.post<ImportExternalVolumeFilesResponse>(
        `${getBackendUrl()}/fileOperations/importExternalVolumeFiles`,
        payload,
      )) as AxiosResponseLike<StandardResponse<unknown>>;
      return response;
    }
  };

  const createMergeEstimateModal = async (
    listOfSelectedFiles: Record<string, FileManagerRow>,
    mergeEstimateName: string,
  ) => {
    const constituentEstimateIdArray = Object.values(listOfSelectedFiles).map((val: FileManagerRow) => val.estimateId);

    const params: Record<string, unknown> = {
      mergeEstimateName,
      spaceId: currentFolder?.spaceId ?? spaceId,
      projectId,
      constituentEstimateIds: constituentEstimateIdArray,
    };

    const response = (await createMergeEstimate(params as Parameters<typeof createMergeEstimate>[0])) as StatusEnvelope;
    if (!response.data.status) {
      dispatch(UnnamedEstimateNotSupportedToast(response.data.errorMessage as TranslatedString));
    } else if (response.data.status) {
      closeFileMergeModal();
      dispatch(
        showToastNotification({
          content: t("Estimates have been merged successfully."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    setOpenMergeEstimateModal(false);
    refreshCurrentFolder();
  };

  const onFileLoad = (files: SelectedFileItem[]) => {
    (files as FileManagerRow[]).forEach((file: FileManagerRow) => {
      if (file?.permission?.read === false && !file.isSpaceRoot) {
        file.isRowDisabled = true;
      }
      if (fileManagerAsPopup && enableOnlyShareableResources && file?.permission?.share === false) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(disableFiles) &&
        ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"].includes(file.resourceType) &&
        disableFiles.includes((file.isEmptyContainer ? file.id : file.fileVersion)!)
      ) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(disableFiles) &&
        file.resourceType == "RESOURCE_TYPE_ISSUE" &&
        (disableFiles as unknown[]).includes(file?.issueVersionId)
      ) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(disableFiles) &&
        file.resourceType == "RESOURCE_TYPE_TASKS" &&
        (disableFiles as (string | undefined)[]).includes(file?.taskId)
      ) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(enableFilesTypes) &&
        !enableFilesTypes.includes(file?.resourceType) &&
        !(enableFilesTypes as (string | undefined)[]).includes(file?.engineType) &&
        !["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"].includes(file?.resourceType)
      ) {
        file.isRowDisabled = true;
      }
    });
  };

  const handleModalClose = () => {
    setOpenContainerModal(false);
    setContainerName("");
    setFileTypeValue("");
    setResourceType({});
    nomenclatureRef.current = [];
    setNomenclatureValues([]);
  };

  const handleCreateContainer = async () => {
    const nameOfContainer = containerName;
    const typeOfContainer = fileTypeValue;
    const fileNomenclatureCorrect = isFileNomenclatureCorrect(nameOfContainer, currentFolder.fileNomenclature);
    if (!fileNomenclatureCorrect) {
      dispatch(
        showToastNotification({
          content: t("File nomenclature is incorrect"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (
      resourceType.value == "RESOURCE_TYPE_EMBED" ||
      (typeof formValidator.validateAll == "function" && formValidator.validateAll())
    ) {
      handleModalClose();
      const customFields: Record<string, unknown> = {};
      (containerFormTemplate as unknown[] as FormTemplateSectionLike[])?.forEach((section: FormTemplateSectionLike) => {
        section.questionFields!.forEach((item) => {
          if ((item.answer as { value?: unknown })?.value) {
            customFields[item._id!] = (item.answer as { value?: unknown }).value;
          }
        });
      });
      const res = (await createContainer({
        name: nameOfContainer,
        type: typeOfContainer,
        hierarchyId: currentFolder.hierarchyId,
        folderType: currentFolder.folderType,
        spaceId: currentFolder?.spaceId ?? spaceId,
        customFields,
        resourceType: resourceType.value,
      })) as StatusEnvelope;
      const response: { content?: string; type?: string } = {};
      if (res.data.status) {
        refreshCurrentFolder();
        response.content = `Container ${nameOfContainer} has been created successfully!`;
      } else {
        response.content = SOMETHING_WENT_WRONG;
        response.type = TOAST_TYPE_ERROR;
      }
      dispatch(showToastNotification(response as { content: TranslatedString; type: string }));
    }
  };

  const handleSaveNewName = async () => {
    const oldName = (openRenameModal as { oldName?: string }).oldName;
    setOpenRenameModal({ state: false, oldName: "" });

    const allParams: Record<string, unknown> = {
      action: "rename",
      path: "/",
      name: oldName,
      newName,
      showFileExtension: true,
      data: [selectedFile],
    };
    beforeSending(allParams);
    if (!allParams.cancel) {
      fileManagerRef.current?.showLoading();
      const res = await axiosWrapper.post<CreateFolderResponse>(`${getBackendUrl()}/fileSystemServer/renameFile`, {
        ...allParams,
      });
      if (!res.data.status) {
        dispatch(
          showToastNotification({
            content: untranslated(res.data?.errorMessage ?? ""),
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        const fileNomenclature = _.get(currentFolder, "fileNomenclature");
        const isNomenclatureCorrect = isFileNomenclatureCorrect(newName, fileNomenclature);
        const newFiles = structuredClone(fileManagerData?.files)?.map((f: FileManagerRow) => {
          if (f.id == (selectedFile as Partial<FileManagerRow>).id) {
            f.name = newName;
            f.isFileNomenclatureCorrect = isNomenclatureCorrect;
            f.dateModified = Date.now();
            f.modifiedBy = userDetails.fullName;
          }
          return f;
        });
        const newObj: Partial<FileManagerData> = {
          files: newFiles,
        };
        if (fileManagerData.filteredFiles?.length) {
          const newFilteredFiles = structuredClone(fileManagerData?.filteredFiles).map((f: FileManagerRow) => {
            if (f.id == (selectedFile as Partial<FileManagerRow>).id) {
              f.name = newName;
              f.isFileNomenclatureCorrect = isNomenclatureCorrect;
            }
            return f;
          });
          newObj.filteredFiles = newFilteredFiles;
        }
        setFileManagerData((prev: FileManagerData) => ({ ...prev, ...newObj }));
        dispatch(
          showToastNotification({
            content: untranslated(`${newName} ${t("renamed successfully")}`),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
      fileManagerRef.current?.hideLoading();
    }
  };

  const handleCloseFolder = () => {
    setOpenFolderCreationModal(false);
    setFolderName("");
  };

  const handleFolderName = (args: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(args.target.value);
  };

  const handleCustomFieldsData = (data: Record<string, unknown>) => {
    setContainerFormTemplate(data as Record<string, unknown> & FormTemplateSection[]);
  };

  const handleEmbedUrl = (args: { name?: string; url?: string; [key: string]: unknown }) => {
    setFileTypeValue((args.target as { value?: string }).value!);
  };

  const handleContainerName = (args: React.ChangeEvent<HTMLInputElement>) => {
    setContainerName(args.target.value);
  };

  const handleCreateFolder = async () => {
    handleCloseFolder();
    const data: Record<string, unknown> = {
      hierarchyId: currentFolder?.spaceId === currentFolder.id ? "0" : currentFolder.id,
      resourceType: "RESOURCE_TYPE_FOLDER",
      spaceId: currentFolder?.spaceId ?? spaceId,
      name: folderName,
      dateModified: Date.now(),
    };
    const allParams: Record<string, unknown> = {
      action: "create",
      path: "/",
      data: [data],
      name: folderName,
    };
    beforeSending(allParams);
    if (!allParams.cancel) {
      fileManagerRef.current?.showLoading();
      // /createFolder takes `data` as a flat object (not the dispatcher's one-element array).
      const res = await axiosWrapper.post<CreateFolderResponse>(`${getBackendUrl()}/fileSystemServer/createFolder`, {
        ...allParams,
        data: (allParams.data as unknown[])[0],
      });
      fileManagerRef.current?.hideLoading();
      if (res.data.status && res.data.isDuplicate) {
        dispatch(
          showToastNotification({
            content: t("Folder with same name already exists."),
            type: TOAST_TYPE_WARNING,
          }),
        );
        refreshCurrentFolder();
      } else if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Folder created successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        refreshCurrentFolder();
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage ?? ""),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
  };

  const handleNomenclatureChange = (
    e: unknown,
    args: { value?: string; itemData?: Record<string, unknown> },
    field: NomenclatureField,
    index: number,
  ) => {
    const value =
      (e as { value?: string; target?: { value?: string } }).value ??
      (e as { target: { value?: string } }).target.value;
    const nomenclatureArr = [...(nomenclatureRef.current ?? [])];

    nomenclatureArr[index] = value!;
    nomenclatureRef.current = nomenclatureArr;
    setNomenclatureValues(nomenclatureArr);

    const separator = _.get(currentFolder, "fileNomenclature.separator", "");
    setContainerName(nomenclatureArr.join(separator));
  };

  const menuOpen = (selectedFiles: SelectedFileItem[] = []) => {
    const disableItems: string[] = [];
    (selectedFiles as FileManagerRow[]).forEach((fileDetail: FileManagerRow) => {
      if (!fileDetail?.permission?.read) {
        disableItems.push("open");
      }
      if (!fileDetail?.permission?.delete) {
        disableItems.push("delete");
      }
      if (
        [
          "RESOURCE_TYPE_FOLDER",
          "RESOURCE_TYPE_SHORTCUT_FILE",
          "RESOURCE_TYPE_MERGED_FILE",
          "RESOURCE_TYPE_CONTRACT",
          "RESOURCE_TYPE_ESTIMATE",
          "RESOURCE_TYPE_SCHEDULE",
        ].includes(fileDetail.resourceType)
      ) {
        disableItems.push("CreateReview");
      }
      // PRD 2: with the portal switch off there is no sharing at all, so both entry points go —
      // ahead of every finer-grained check, which would otherwise re-enable them per row.
      if (!isExternalSharingEnabled(layout)) {
        disableItems.push("createExternalLink");
        disableItems.push("manageExternalLinks");
      }
      // Shareable external links (CP-283) are Files and Folders only in v1 — sharing at Space or
      // Project level is an explicit non-goal, and estimates/schedules/contracts/transmittals are
      // not files. The server's resourceType enum refuses them too; this keeps the action off the
      // menu rather than surfacing it and then failing.
      if (
        !["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FOLDER"].includes(fileDetail.resourceType)
      ) {
        disableItems.push("createExternalLink");
        disableItems.push("manageExternalLinks");
      }
      if (
        ![
          "RESOURCE_TYPE_MERGED_FILE",
          "RESOURCE_TYPE_FILE",
          "RESOURCE_TYPE_FOLDER",
          "RESOURCE_TYPE_SHORTCUT_FILE",
        ].includes(fileDetail.resourceType) ||
        !fileDetail?.permission?.download ||
        fileDetail.isEmptyContainer
      ) {
        disableItems.push("download");
      }
      if (!fileDetail?.permission?.share) {
        disableItems.push("transmit");
        disableItems.push("CreateReview");
        // Coarse pre-filter only. Creating/managing an external link actually requires FULL ACCESS
        // (RESOURCE_CHANGE_PERMISSION, held by that role alone), but the row's `permission` object
        // carries no flag for it — `resolvePermissions` never maps permission id 9. Share is the
        // closest available proxy and no full-access user lacks it, so this hides the actions from
        // users who certainly cannot use them; the server remains the authority and answers a
        // non-full-access caller with an explicit message.
        disableItems.push("createExternalLink");
        disableItems.push("manageExternalLinks");
      }
      if (!fileDetail?.permission?.write) {
        disableItems.push("rename");
        disableItems.push("cut");
        disableItems.push("newFolder");
      }
      if (!fileDetail?.permission?.upload) {
        disableItems.push("upload");
      }
      if (fileDetail?.hierarchyId == "0") {
        disableItems.push("cut");
      }
      if (
        !DRAWING_TYPE.includes(fileDetail?.name.split(".")[fileDetail?.name.split(".").length - 1]!) &&
        fileDetail?.resourceType !== "RESOURCE_TYPE_MERGED_FILE"
      ) {
        disableItems.push("preloadAndOpen");
        disableItems.push("loadWithoutProperties");
      }
      if (fileDetail?.isFile) {
        disableItems.push("paste");
        if (fileManagerAsPopup) {
          disableItems.push("open");
          disableItems.push("preloadAndOpen");
          disableItems.push("loadWithoutProperties");
        }
      }
      if (!fileDetail?.isFile || fileDetail?.isContainer || !fileDetail?.permission?.download) {
        disableItems.push("copy");
      }
    });
    if (!filesToCopy && !filesToCut) {
      disableItems.push("paste");
    }
    // Pasting across Spaces is not allowed: if the copied/cut files belong to a different Space than the
    // paste target (the right-clicked folder, else the current folder), disable paste. Only triggers when
    // both Spaces are known and differ (single-space mode is unaffected).
    const clipboardItems = filesToCopy || filesToCut;
    const clipboardSpaceId = clipboardItems?.[0]?.spaceId;
    const pasteTargetSpaceId = (selectedFiles?.[0] ?? currentFolder)?.spaceId ?? currentFolder?.spaceId;
    if (clipboardSpaceId && pasteTargetSpaceId && String(clipboardSpaceId) !== String(pasteTargetSpaceId)) {
      disableItems.push("paste");
    }
    if (selectedFiles.length > 1) {
      disableItems.push("open");
      disableItems.push("rename");
      disableItems.push("paste");
      // A link points at exactly one file or folder; there is no multi-item link in v1.
      disableItems.push("createExternalLink");
      disableItems.push("manageExternalLinks");
    }
    if (fileManagerAsPopup) {
      disableItems.push("upload");
      disableItems.push("new");
    }
    const uniqueDisableItems = [...new Set(disableItems)];
    return uniqueDisableItems;
  };

  // ── Shareable external links (CP-283) ─────────────────────────────────────
  //
  // The dialog flow lives in its own hook; this file only turns a selected grid row into the
  // `ExternalLinkTarget` those dialogs need, and hands the flow object back to the component so it
  // can render `<ExternalLinksHost/>`.
  const externalLinks = useExternalLinks();

  /**
   * Map a right-clicked row to a link target, VALIDATING as it converts.
   *
   * This is the boundary between the grid's loosely-typed rows and the server's create-request
   * contract, so the ids are parsed into branded `ObjectIdString`s and the resource type is narrowed
   * to the three shareable kinds here — rather than asserted away further down. A row that fails
   * either check yields `null` and the action simply does nothing, instead of firing a request the
   * server is certain to reject.
   *
   * `spaceId` falls back to the current folder's: a row read through the project tree does not always
   * carry its own, and the server needs it for the full-access check.
   */
  const toExternalLinkTarget = (selectedFiles: FileManagerRow[]): ExternalLinkTarget | null => {
    const file = selectedFiles?.[0];
    if (!file) return null;
    // Id validation and the shareable-kind check live in `buildExternalLinkTarget`, shared with the
    // details-panel section, so both surfaces answer "can this be shared?" the same way. Only the
    // three-way spaceId fallback is specific to a grid row.
    return buildExternalLinkTarget({
      resourceId: String(file.id ?? file.hierarchyId ?? ""),
      spaceId: String(file.spaceId ?? currentFolder?.spaceId ?? spaceId ?? ""),
      resourceType: file.resourceType,
      name: file.name,
    });
  };

  const handleCreateExternalLink = (selectedFiles: FileManagerRow[]) => {
    const target = toExternalLinkTarget(selectedFiles);
    if (!target) return;
    externalLinks.openCreate(target);
  };

  const handleManageExternalLinks = (selectedFiles: FileManagerRow[]) => {
    const target = toExternalLinkTarget(selectedFiles);
    if (!target) return;
    externalLinks.openManage(target);
  };

  // File manager options mapper
  const fileManagerOptionsMapper: Record<string, FileManagerMenuOption> = {
    layoutOptions: [
      {
        id: "refresh",
        name: "Refresh",
        icon: "refresh",
        onClick: refreshCurrentFolder,
      },
      {
        id: "paste",
        name: "Paste",
        icon: "Duplicate",
        onClick: handleResourcePaste,
      },
      {
        id: "selectAll",
        name: "Select All",
        icon: "selectAll",
        onClick: handleSelectAllOption,
      },
      {
        id: "unSelectAll",
        name: "Un-Select All",
        icon: "unSelectAll",
        onClick: handleUnSelectAllOption,
      },
    ],
    fileOptions: [
      {
        id: "open",
        name: "Open",
        icon: "folder",
        onClick: (selectedFiles: FileManagerRow[]) => handleOpenOrPreloadAndOpen(selectedFiles, "open"),
      },
      {
        id: "preloadAndOpen",
        name: "Preload & Open",
        icon: "folder",
        onClick: (selectedFiles: FileManagerRow[]) => handleOpenOrPreloadAndOpen(selectedFiles, "preloadAndOpen"),
      },
      {
        id: "loadWithoutProperties",
        name: "Load without properties",
        icon: "folder",
        onClick: (selectedFiles: FileManagerRow[]) =>
          handleOpenOrPreloadAndOpen(selectedFiles, "loadWithoutProperties"),
      },
      {
        id: "delete",
        name: "Delete",
        icon: "delete",
        onClick: handleDeleteClick,
      },
      {
        id: "download",
        name: "Download",
        icon: "download",
        onClick: handleDownloadFile,
      },
      {
        id: "rename",
        name: "Rename",
        icon: "editProfile",
        onClick: handleRenameFile,
      },
      {
        id: "cut",
        name: "Cut",
        icon: "cut",
        onClick: handleResourceCut,
      },
      {
        id: "copy",
        name: "Copy",
        icon: "copy",
        onClick: handleResourceCopy,
      },
      {
        id: "transmit",
        name: "Transmit",
        icon: "Transmittals",
        onClick: handleFileTransmit,
      },
      {
        // Only shown for CAD drawings (gated by `extensions` in useFileManagerFiles).
        id: "exportToPdf",
        name: t("Export to PDF"),
        icon: "download",
        extensions: ["dwg", "dxf"],
        onClick: handleExportToPdf,
      },
      {
        id: "CreateReview",
        name: "Create a Review",
        icon: "Review",
        onClick: () => setCreateReviewModal({ show: true }),
      },
      {
        id: "createExternalLink",
        name: t("Create shared link"),
        icon: "Link",
        onClick: handleCreateExternalLink,
      },
      {
        id: "manageExternalLinks",
        name: t("Manage shared links"),
        icon: "sharedView",
        onClick: handleManageExternalLinks,
      },
    ],
    folderOptions: [
      {
        id: "open",
        name: "Open",
        icon: "folder",
        onClick: (selectedFiles: FileManagerRow[]) => handleOpenOrPreloadAndOpen(selectedFiles, "open"),
      },
      {
        id: "delete",
        name: "Delete",
        icon: "delete",
        onClick: handleDeleteClick,
      },
      {
        id: "download",
        name: "Download",
        icon: "download",
        onClick: handleDownloadFile,
      },
      {
        id: "rename",
        name: "Rename",
        icon: "editProfile",
        onClick: handleRenameFile,
      },
      {
        id: "cut",
        name: "Cut",
        icon: "cut",
        onClick: handleResourceCut,
      },
      {
        id: "paste",
        name: "Paste",
        icon: "Duplicate",
        onClick: handleResourcePaste,
      },
      {
        id: "createExternalLink",
        name: t("Create shared link"),
        icon: "Link",
        onClick: handleCreateExternalLink,
      },
      {
        id: "manageExternalLinks",
        name: t("Manage shared links"),
        icon: "sharedView",
        onClick: handleManageExternalLinks,
      },
    ],
  };

  // Dropdown button items
  const dropDownButtonItems: {
    text?: string;
    id: string;
    subItems?: { text?: string; id: string; [key: string]: unknown }[];
    [key: string]: unknown;
  }[] = [
    ...(importOnlyUploads
      ? []
      : [
          { text: t("Upload File"), id: "fileUpload", iconCss: "e-upload-file", iconName: "uploadFile" },
          { text: t("Upload Folder"), id: "folderUpload", iconCss: "e-upload-folder", iconName: "uploadFolder" },
          { seperator: true, id: "seperator1" },
        ]),
    { text: t("New Folder"), id: "newFolder", iconCss: "e-new-folder", iconName: "newFolder" },
    { seperator: true, id: "seperator2" },
    { text: t("Merge"), id: "merge", iconCss: "e-new-merge", iconName: "merge" },
    { text: t("Container"), id: "container", iconCss: "e-new-container", iconName: "container" },
  ];

  if (showExcelImport) {
    dropDownButtonItems.push({
      text: t("Import"),
      id: "import",
      iconCss: "e-new-importExcel",
      iconName: "ImportExcel",
      hasSubmenu: true,
      subItems: [
        { text: t("Container"), id: "importContainer", iconName: "container" },
        { text: t("File"), id: "importFile", iconName: "files" },
      ],
    });
  }
  if (layout?.ATTACHED_EXTERNAL_VOLUME && !importOnlyUploads) {
    dropDownButtonItems.push({
      text: t("Import"),
      id: "fileImport",
      iconCss: "e-upload-file",
      iconName: "uploadFile",
    });
  }

  // Compose data layer (used directly by mobile; web's Syncfusion drives data so auto-load is disabled)
  const fileManagerComponent = useFileManagerComponent({
    fileManagerData,
    setFileManagerData,
    setCurrentFolder,
    beforeSending,
    onSuccess,
    showFolderTree,
    path: path ?? "/",
    workspaceId: spaceId,
    disableAutoLoad: !nativeMobile,
  });

  const fileManagerSharedState = useFileManagerShared({
    setSelectedFolder: fileManagerComponent.setSelectedFolder,
    fileManagerData,
    tempFolder: fileManagerComponent.tempFolder,
    handleSearchFiles: fileManagerComponent.handleSearchFiles,
    selectedFolder: fileManagerComponent.selectedFolder,
    preSearchText: preSearchText ?? "",
    onFileLoad,
  });

  // Effects
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setPreSearchText(fileSearchText);
  }, [fileSearchText]);

  useEffect(() => {
    if (!defaultPath) {
      setPath("/");
    } else {
      setPath(defaultPath);
    }
  }, [defaultPath]);

  useEffect(() => {
    if (nativeMobile || typeof document === "undefined") return;
    document.title = `Files | ${projectData?.description}`;
  }, [projectData?.description, nativeMobile]);

  useEffect(() => {
    if (refreshFileManager) {
      dispatch(refreshFileManagerFn(false));
      refreshCurrentFolder();
    }
    return () => {
      dispatch(refreshFileManagerFn(false));
    };
  }, [refreshFileManager]);

  useEffect(() => {
    getFormTemplate();
    dispatch(setCurrentFolderInFileManager(currentFolder));
    if (nativeMobile) return;
    // Skip the first run (mount): the initial currentFolder ({id:"0"}) would clear a deep-link hash from the
    // URL before a browser-refresh restore can read it. Once the app sets the real currentFolder (initial
    // load or restore), subsequent runs write/clear the hash normally.
    if (!hashWriterInitRef.current) {
      hashWriterInitRef.current = true;
      return;
    }
    if (!fileManagerAsPopup && !currentFolder.isSpaceRoot) {
      const innerPath = currentFolder.parentIdArray?.filter((id: string) => id != "0").join("/");
      const initialPath = innerPath ? `/${innerPath}` : "";
      // Project ("Folders") mode: prefix the Space id so a browser refresh can re-focus the right Space —
      // a folder's parentIdArray does not contain its Space id. Space-root nodes already have id === spaceId,
      // so they need no prefix.
      const spacePrefix =
        isProjectMode && currentFolder?.spaceId && !currentFolder?.isSpaceRoot ? `/${currentFolder.spaceId}` : "";
      const folderPath = `${spacePrefix}${initialPath}/${currentFolder.id}/`;
      const encodedFolderPath = encodeURIComponent(folderPath);
      navigate(`${location.pathname}${location.search}#folderPath=${encodedFolderPath}`, { replace: true });
    } else if (!fileManagerAsPopup && currentFolder.isSpaceRoot) {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [currentFolder]);

  useEffect(() => {
    if (fileUploadState == "CLOSED") {
      dispatch(setIsFileVersionUploading(false));
      dispatch(closeFileUpload());
      dispatch(emptyFileUploadData());
    }
  }, [fileUploadState]);

  useEffect(() => {
    fileUploadListRef.current = [...(fileUploadList ?? [])];
  }, [fileUploadList]);

  useEffect(() => {
    if (fileUploadState === "CLOSED") return;
    if ((fileUploadList ?? []).length) {
      dispatch(minimiseFileUpload());
    } else {
      dispatch(closeFileUpload());
    }
  }, [location]);

  useEffect(() => {
    // Portal-level upload restriction (`portals.metaData.ALLOWED_FILE_TYPES`). Absent/null means
    // "no restriction", which is the empty accept string. Built from the map's KEYS as dotted
    // extensions (".pdf,.dwg") — not its MIME values, which would hide .dwg/.rvt/.nwd from the OS
    // file dialog entirely, since the master list maps all five binary CAD formats to
    // application/octet-stream.
    setFileAllowedExtensions({
      minFileSize: 0,
      maxFileSize: MAX_FILE_UPLOAD_SIZE,
      allowedExtensions: buildAllowedExtensionsAccept(layout?.ALLOWED_FILE_TYPES),
    });
  }, [layout?.ALLOWED_FILE_TYPES]);

  useEffect(() => {
    return () => {
      if (typeof handleFileSelectionCallback === "function") {
        handleFileSelectionCallback([]);
      }
      if (!fileManagerAsPopup) {
        dispatch(emptyCurrentFolderInFileManager());
        dispatch(setSpaceData({}));
      }
    };
  }, []);

  useEffect(() => {
    reloadFileManager();
    setToolBarBtnState((prev) => ({
      ...prev,
      upload: !hideUpload,
      refresh: true,
      rename: prev.rename,
      delete: prev.delete,
      download: prev.download,
      details: true,
    }));
  }, [hideUpload]);

  useEffect(() => {
    if (Array.isArray(alreadySelected) && fileManagerRef.current) {
      const currentFiles = fileManagerRef.current?.dataView?.items || []; // ? because dataView is null in case of large icons mode
      const selectTheseRows: number[] = [];
      (alreadySelected as FileManagerRow[]).forEach((file: FileManagerRow) => {
        const indexOfItem = currentFiles.findIndex((x: FileManagerRow) => x.hierarchyId == file.hierarchyId);
        if (!isNaN(indexOfItem) && indexOfItem != -1) {
          selectTheseRows.push(indexOfItem);
        }
      });
      fileManagerRef.current?.setSelectedRows?.(selectTheseRows);
    }
  }, [fileManagerRef.current, currentFolder]);

  useEffect(() => {
    // The hidden-items toggle is a single-space construct; project mode has no ambient space to toggle.
    if (spaceId && !isProjectMode) {
      dispatch(setSpaceToggle({ spaceId }));
      reloadFileManager();
    }
  }, [spaceId, isProjectMode]);

  return {
    // Refs
    fileManagerRef,
    formTemplateRef,
    formValidator,
    fileUploadListRef,
    nomenclatureRef,
    spaceListRef,
    tabRef,
    pollingRef,

    // State
    fileManagerData,
    setFileManagerData,
    showContainerImportModal,
    setShowContainerImportModal,
    importMode,
    setImportMode,
    openRenameModal,
    setOpenRenameModal,
    selectedFile,
    setSelectedFile,
    newName,
    setNewName,
    folderName,
    setFolderName,
    createReviewModal,
    setCreateReviewModal,
    fileAllowedExtensions,
    setFileAllowedExtensions,
    openMergeModal,
    setOpenMergeModal,
    openMergeEstimateModal,
    setOpenMergeEstimateModal,
    openFolderCreationModal,
    setOpenFolderCreationModal,
    openFileImport,
    setOpenFileImport,
    openContainerModal,
    setOpenContainerModal,
    currentFolder,
    setCurrentFolder,
    selectedFilesToDelete,
    setSelectedFilesToDelete,
    openDeleteConfirmationModal,
    setOpenDeleteConfirmationModal,
    openCreateTransmittalModal,
    setOpenCreateTransmittalModal,
    filesToCopy,
    setFilesToCopy,
    filesToCut,
    setFilesToCut,
    formTemplateReceived,
    setFormTemplateReceived,
    containerFormTemplate,
    setContainerFormTemplate,
    fileTypeValue,
    setFileTypeValue,
    resourceType,
    setResourceType,
    containerName,
    setContainerName,
    draggedFiles: draggedFiles as File[] | undefined,
    setDraggedFiles,
    path,
    setPath,
    preSearchText,
    setPreSearchText,
    toolBarBtnState,
    setToolBarBtnState,
    anchorEl,
    setAnchorEl,
    open,
    importSubmenuAnchor,
    importSubmenuOpen,

    // Redux state
    layout,
    getSpaceToggle,
    projectData,
    userDetails,
    fileUploadList,
    isFileVersionUploading,
    fileUploadState,
    spaceData,
    refreshFileManager,

    // Handlers
    showToast,
    handleDeleteClick,
    handleDeleteModalClose,
    handleDeleteSuccess,
    getSelectedFilesInFileManager,
    handleRenameFile,
    handleFileTransmit,
    fetchConstituentFilesOfMergedFile,
    handleLocationOpen,
    handleOpenOrPreloadAndOpen,
    handleDownloadFile,
    getPermission,
    beforeSending,
    handleEmptyStateDrop,
    handleEmptyStateDragOver,
    handleResourceCut,
    handleResourceCopy,
    handleResourcePaste,
    handleSelectAllOption,
    handleUnSelectAllOption,
    reloadFileManager,
    refreshCurrentFolder,
    handleSpaceSelect,
    handleSpaceToggle,
    handleDetailIconClick,
    getFormTemplate,
    handleFileAndFolderUpload,
    handleSelectOption,
    handleClick,
    handleClose,
    handleImportSubmenuOpen,
    handleImportSubmenuClose,
    closeReviewModal,
    onFileSelect,
    onFileClick,
    toolbarClick,
    onSuccess,
    saveFileMergeModal,
    closeFileMergeModal,
    closeFileImportPopup,
    closeMergeEstimateModal,
    handleFileImportCallback,
    createMergeEstimateModal,
    onFileLoad,
    handleModalClose,
    handleCreateContainer,
    handleSaveNewName,
    handleCloseFolder,
    handleFolderName,
    handleCustomFieldsData,
    handleEmbedUrl,
    handleContainerName,
    handleCreateFolder,
    handleNomenclatureChange,
    nomenclatureValues,
    menuOpen,

    // External links (CP-283) dialog flow, rendered by FileManagerNew via <ExternalLinksHost/>
    externalLinks,
    pendingMoveWarning,
    setPendingMoveWarning,
    confirmPendingMove,

    // Config
    fileManagerOptionsMapper,
    dropDownButtonItems,

    // Props passed through
    fileManagerAsPopup,
    hideAllTabs,
    hideUpload,
    hideExternalSpace,
    hideReviews,
    enableSpaceListDropdown,
    disableContextMenu,
    showFolderTree,
    disableDragAndDropUpload,
    showExcelImport,
    mobileFileManager,
    enableOnlyShareableResources,
    disableFiles,
    enableFilesTypes,
    createShortcut,
    allowFolderSelectionCallback,
    spaceList,
    spaceId,
    projectId,
    projectMode: isProjectMode,

    // Composed data layer (used by mobile)
    fileManagerComponent,
    fileManagerSharedState,

    // Top-level re-exports from the composed data layer (so mobile can use only useFileManager)
    selectedFolder: fileManagerComponent.selectedFolder,
    setSelectedFolder: fileManagerComponent.setSelectedFolder,
    tempFolder: fileManagerComponent.tempFolder,
    refreshGrid: fileManagerComponent.refreshGrid,
    isLoaded: fileManagerComponent.isLoaded,
    refreshFiles: fileManagerComponent.refreshFiles,
    deleteFiles: fileManagerComponent.deleteFiles,
    isFolder: fileManagerComponent.isFolder,
    canViewFile: fileManagerComponent.canViewFile,
    canDeleteFile: fileManagerComponent.canDeleteFile,
    breadCrumbData: fileManagerSharedState.breadCrumbData,
    filesData: fileManagerSharedState.filesData,
    query: fileManagerSharedState.query,
    setQuery: fileManagerSharedState.setQuery,
    showPathColumn: fileManagerSharedState.showPathColumn,

    // Translation
    t,
  };
}

export default useFileManager;
